import React, { useContext, useEffect, useRef, useState } from 'react'
import { useCountdown } from 'usehooks-ts'
import anime from 'animejs'
import delay from 'delay'
import moment from 'moment'
import { Button, Paper, useTheme, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, CircularProgress } from '@mui/material'

import WhatsAppIcon from '@mui/icons-material/WhatsApp'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LightbulbIcon from '@mui/icons-material/Lightbulb'

import QrCodeIcon from '@mui/icons-material/QrCode'
import StorageIcon from '@mui/icons-material/Storage'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'
import AnimatedScan from '../AnimatedScan'
import CircularProgressCustom from '../CircularProgressCustom'
import LinearProgressCustom from '../LinearProgressCustom'
import TextFieldCustom from '../TextFieldCustom'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import MetaOficialUpgradeNotice from './MetaOficialUpgradeNotice'
import MetaOficialBenefits from './MetaOficialBenefits'
import MetaEmbeddedSignupButton, { isEmbeddedSignupAvailable } from './MetaEmbeddedSignupButton'


const API_VERSIONS_NEED_QRCODE = ['hiperapi-v1', 'hiperapi-v1-next', 'hiperapi-v2']
const API_VERSIONS_OFICIAL = ['hiperapi-v3-cloud-official']


function DeviceDisconnected({ onQrCodeGenerated }) {
  const theme = useTheme()
  const [count, { startCountdown, stopCountdown, resetCountdown }] = useCountdown({ countStart: 5, intervalMs: 1000 })
  const {
    getCredentials,
    provider,
    server,
    fetchData: getApiData,
    whatsappBusinessId: businessId,
    whatsappBusinessNumberId: businessNumberId,
    whatsappBusinessToken: businessToken,
    user
  } = useContext(ApiDataWrapperContext)
  const hasMetaOficial = user?.plan?.functionalities?.includes('WhatsappCloudApi') || false
  const upgradeContact = user?.upgradeContact || null
  // Login com Facebook (ES v4): só entra em cena com plano habilitado + as duas
  // envs do app da Meta configuradas. Sem isso, some silenciosamente e o
  // formulário manual (Number ID/Business ID/Token) continua sendo o único caminho.
  const showEmbeddedSignup = hasMetaOficial && isEmbeddedSignupAvailable()
  const { api } = getCredentials()
  const containerRef = useRef()
  const [animateQrCodeScan, setAnimateQrCodeScan] = useState(false)
  const [qrCode, setQrCode] = useState('')
  const [qrCodeUpdatedAt, setQrCodeUpdatedAt] = useState('')
  const [apiNotFoundError, setApiNotFoundError] = useState(false)
  const [apiQrCodeInterval, setApiQrCodeInterval] = useState(undefined)
  const [apiVersion, setApiVersion] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSwitchingProvider, setIsSwitchingProvider] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(true)
  const [whatsappBusinessId, setWhatsappBusinessId] = useState('')
  const [whatsappBusinessNumberId, setWhatsappBusinessNumberId] = useState('')
  const [whatsappBusinessToken, setWhatsappBusinessToken] = useState('')
  // Recuperação 1-clique: reseta a sessão travada/morta e gera um QR novo.
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [resetCooldown, setResetCooldown] = useState(0) // segundos de espera pós-reset
  const [resetSnackbar, setResetSnackbar] = useState('')
  const [tokenSaveError, setTokenSaveError] = useState('')

  const handleAnimation = async () => {
    await delay(300)
    anime({
      targets: containerRef.current,
      opacity: [0, 1],
      translateY: [20, 0],
      scale: [0.99, 1]
    })
    await delay(300)
    setAnimateQrCodeScan(true)
  }

  const getApiQrCode = async () => {
    try {
      setApiNotFoundError(false)
      const { api, secret } = getCredentials()
      let response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)

      if(response?.statusSession === 'isLogged') {
        return
      }

      await delay(1000)

      if(apiVersion === 'hiperapi-v1' || apiVersion === 'hiperapi-v1-next') {
        // canário usa o mesmo fluxo Baileys, passando o provider pra não ser resetado pra v1
        response = await hipersendService.notificacoesInteligentes.getQrCodeHiperApiV1(api, secret, apiVersion)
      } else if(apiVersion === 'hiperapi-v2') {
        response = await hipersendService.notificacoesInteligentes.getQrCodeHiperApiV2(api, secret)
      } else if(apiVersion === 'hiperapi-v3-cloud-official') {
        response = {}
      } else {
        resetCountdown()
        setQrCode('')
        setQrCodeUpdatedAt('')
        return
      }

      resetCountdown()
      startCountdown()
      setQrCode(response.base64Qr)
      setQrCodeUpdatedAt(response.updated_at)
      setAnimateQrCodeScan(true)

      if(typeof onQrCodeGenerated === 'function') {
        onQrCodeGenerated(response.base64Qr)
      }

      if(provider !== apiVersion) {
        await getApiData()
      }
    } catch (error) {
      console.error(error)
      const errorMessage = error?.response?.data?.error

      if(errorMessage === 'Não foi possível encontrar a instância') {
        setApiNotFoundError(true)
      }
    } finally {
      setIsLoading(false)
      setIsSwitchingProvider(false)
    }
  }

  const saveApiOficialCredentials = async () => {
    setTokenSaveError('')

    // token mascarado no contexto ('••••XXXX') = já existe uma credencial de
    // verdade vinda do Embedded Signup. Campo de token vazio aqui NUNCA pode
    // virar um POST com token:'' — isso destruiria a credencial real em
    // silêncio (o cliente só vê o placeholder e clica Salvar sem preencher
    // nada, achando que só está atualizando Number ID/Business ID).
    if (!whatsappBusinessToken.trim() && businessToken && businessToken.includes('•')) {
      setTokenSaveError('Cole um token novo para trocar a credencial, ou reconecte pelo botão Entrar com Facebook.')
      return
    }

    try {
      setIsLoading(true)
      setApiNotFoundError(false)
      const { api, secret } = getCredentials()
      let response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)

      if(response?.statusSession === 'isLogged') {
        return
      }

      await delay(1000)

      if(apiVersion !== 'hiperapi-v3-cloud-official') {
        resetCountdown()
        setQrCode('')
        setQrCodeUpdatedAt('')
        return
      }

      response = await hipersendService.notificacoesInteligentes.whatsappBusinessConnectionHiperApiV3CloudApi(api, secret, {
        businessId: whatsappBusinessId,
        numberId: whatsappBusinessNumberId,
        token: whatsappBusinessToken
      })

      await delay(1000)
      await getApiData()
    } catch (error) {
      console.error(error)
      const errorMessage = error?.response?.data?.error

      if(errorMessage === 'Não foi possível encontrar a instância') {
        setApiNotFoundError(true)
      }
    } finally {
      setIsLoading(false)
      setIsSwitchingProvider(false)
    }
  }
  
  const resetQrCode = () => {
    setQrCode('')
    setQrCodeUpdatedAt('')
  }

  const updateProvider = async provider => {
    try {
      const { api, secret } = getCredentials()
      await hipersendService.notificacoesInteligentes.updateProvider(api, secret, provider)
      getApiData()
    } catch (error) {
      console.error(error)
    }
  }

  // Reseta a sessão (limpa a credencial morta no servidor) e força um QR novo.
  // Usa o endpoint expireQRCode que já existe; o polling existente busca o QR fresco.
  const handleResetSession = async () => {
    try {
      setConfirmResetOpen(false)
      setIsResetting(true)
      setIsLoading(true)
      const { api, secret } = getCredentials()
      await hipersendService.notificacoesInteligentes.expireQrCode(api, secret)
      setQrCode('')
      setQrCodeUpdatedAt('')
      setAnimateQrCodeScan(false)
      resetCountdown()
      startCountdown()
      // cooldown evita reset em série — reconexão em massa gera throttle de IP no WhatsApp
      setResetCooldown(45)
      setResetSnackbar('Novo QR Code gerado! Escaneie abaixo com o WhatsApp.')
      await delay(1500)
      getApiQrCode()
    } catch (error) {
      console.error(error)
      setResetSnackbar('Não foi possível gerar o QR Code. Tente novamente em instantes.')
    } finally {
      setIsResetting(false)
    }
  }

  useEffect(() => {
    if(!apiVersion) return
    getApiQrCode()
  }, [apiVersion])

  useEffect(() => {
    if(shouldAnimate && (qrCode || apiVersion === 'hiperapi-v3-cloud-official')) {
      handleAnimation()
      setShouldAnimate(false)
    }
  }, [qrCode])

  useEffect(() => {
    setApiVersion(provider)
  }, [provider])

  useEffect(() => {
    const apiVersionNeedQrCode = API_VERSIONS_NEED_QRCODE.includes(apiVersion)

    if(!apiVersionNeedQrCode) {
      resetCountdown()
      resetQrCode()
      return
    }

    // não requisita QR com a aba em segundo plano (reduz tráfego pelo proxy)
    if(document.hidden) {
      stopCountdown()
      return
    }

    if(count === 0) {
      setIsLoading(true)
      getApiQrCode()
      resetCountdown()
    }
  }, [count])

  // pausa/retoma polling de QR code conforme visibilidade da aba
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopCountdown()
      } else if (API_VERSIONS_NEED_QRCODE.includes(apiVersion)) {
        resetCountdown()
        startCountdown()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [apiVersion])

  useEffect(() => {
    return () => resetCountdown()
  }, [])

  useEffect(() => {
    setWhatsappBusinessId(businessId)
    setWhatsappBusinessNumberId(businessNumberId)
    // conectado via Embedded Signup, o token que volta do backend vem MASCARADO
    // ('••••XXXX') — reenviar esse valor no formulário manual quebraria a
    // credencial. Deixa vazio com placeholder pra só sobrescrever se o cliente
    // colar um token novo de verdade.
    setWhatsappBusinessToken(businessToken && businessToken.includes('•') ? '' : businessToken)
    setTokenSaveError('')
  }, [businessId, businessNumberId, businessToken])

  useEffect(() => {
    if(isLoading) {
      stopCountdown()
    }
  }, [isLoading])

  // contagem regressiva do cooldown do botão "Gerar novo QR Code"
  useEffect(() => {
    if(resetCooldown <= 0) return
    const timer = setTimeout(() => setResetCooldown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resetCooldown])

  if(apiNotFoundError) {
    return (
      <div>
        api não encontrada
      </div>
    )
  }

  return (
    <div className="relative min-h-[640px]">
      {
        !qrCode && API_VERSIONS_NEED_QRCODE.includes(apiVersion) &&
        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
          <div className="flex flex-col justify-center items-center">
            <CircularProgressCustom />
            <Text className="mt-4 font-bold text-sm">Gerando dados para integração</Text>
          </div>
        </div>
      }
      {
        API_VERSIONS_OFICIAL.includes(apiVersion) && hasMetaOficial &&
        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
          <div className="flex flex-col justify-center items-center">
            <CircularProgressCustom />
            <Text className="mt-4 font-bold text-sm">Gerando dados para integração</Text>
          </div>
        </div>
      }
      <div ref={containerRef} className="opacity-0">
        <Paper elevation={5}>
          <Paper elevation={0}>
            {
              isLoading &&
              <div className="absolute top-0 left-0 w-full">
                <LinearProgressCustom />
              </div>
            }
            <div className="w-full mx-auto p-5 sm:p-8 xl:p-12">
              {/* Empilha por padrão; vira 2 colunas só no xl, onde há largura pra ambas */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-y-10 xl:gap-x-12">
                <div className="col-span-1 xl:col-span-5">
                  <div className="mb-4">
                    <div className="flex items-center space-x-1 mb-1">
                      <QrCodeIcon />
                      <Text className="px-1">Versão da API</Text>
                    </div>
                    <Select
                      className="w-full"
                      sx={{
                        select: {
                          '.MuiOutlinedInput-notchedOutline': {
                            borderWidth: '10px !important',
                          }
                        }
                      }}
                      labelId="hiperapi-version-label"
                      id="hiperapi-version"
                      value={apiVersion}
                      onChange={event => {
                        const nextVersion = event.target.value
                        const isMetaOficial = API_VERSIONS_OFICIAL.includes(nextVersion)
                        setAnimateQrCodeScan(false)
                        setApiVersion(nextVersion)
                        if (isMetaOficial && !hasMetaOficial) {
                          setIsLoading(false)
                          setIsSwitchingProvider(false)
                          return
                        }
                        setIsLoading(true)
                        setIsSwitchingProvider(true)
                        updateProvider(nextVersion)
                      }}
                    >
                      <MenuItem value="hiperapi-v1">HiperAPI V1 - Whatsapp Web</MenuItem>
                      {/*
                        Canário (GLAWaba V2 - QRCode PRO) parqueado: só faz sentido
                        quando o BAILEYS_NEXT_WA_VERSION apontar pra uma versão nova
                        de verdade. Com ele igual à estável não agrega, e trocar a
                        versão por baixo de uma sessão pareada desloga o número (401).
                        Pra religar: descomentar aqui + garantir re-pareamento limpo.
                      <MenuItem value="hiperapi-v1-next">
                        <span className="flex items-center space-x-2">
                          <span>GLAWaba V2 - QRCode PRO</span>
                          <span
                            className="px-1 font-black text-xs rounded-sm text-white"
                            style={{ backgroundColor: theme.palette.success?.main || '#22C55E' }}
                          >MENOS BLOQUEIOS</span>
                        </span>
                      </MenuItem>
                      */}
                      <MenuItem value="hiperapi-v3-cloud-official">
                        <span className="flex items-center space-x-2">
                          <span>HiperAPI - WhatsApp API Oficial</span>
                          <span
                            className="px-1 font-black text-xs rounded-sm text-white"
                            style={{ backgroundColor: theme.palette.primary.main }}
                          >NOVIDADE!</span>
                        </span>
                      </MenuItem>
                    </Select>
                  </div>
                  <Collapse in={API_VERSIONS_NEED_QRCODE.includes(apiVersion)}>
                    <Collapse in={qrCode && apiVersion === 'hiperapi-v2'} timeout="auto" unmountOnExit>
                      <div className="mb-4 flex space-x-2 p-4 rounded shadow" style={{ backgroundColor: theme.palette.primary.main }}>
                        <LightbulbIcon style={{ fontSize: 35 }} />
                        <Text className="leading-none font-bold">
                          Recomendado utilizar Whatsapp Business para uma melhor experiência
                        </Text>
                      </div>
                    </Collapse>
                    <div style={{ opacity: isLoading ? 0.3 : 1 }}>
                      <Text>Verificando qrcode em {count}</Text>
                    </div>
                    <div className="relative w-fit mx-auto xl:mx-0">
                      {
                        isSwitchingProvider && API_VERSIONS_NEED_QRCODE.includes(apiVersion) &&
                        <div className="absolute top-0 left-0 w-full h-full bg-[#8a8a8a32] z-10" />
                      }
                      <div style={{ opacity: isSwitchingProvider ? 0.025 : 1 }}>
                        {/* QR fluido: escala com a coluna e nunca estoura em desktop menor/mobile */}
                        <AnimatedScan
                          animate={animateQrCodeScan}
                          className="w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] xl:w-[360px] xl:h-[360px]"
                        >
                          <div className="w-full h-full">
                            <img className="w-full h-full object-contain" src={qrCode} />
                          </div>
                        </AnimatedScan>
                      </div>
                      {
                        isSwitchingProvider &&
                        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
                          <div
                            className="w-[45px] h-[45px] rounded-full shadow flex justify-center items-center"
                            style={{
                              backgroundColor: theme.palette.background.paper
                            }}
                          >
                            <CircularProgressCustom size={25} />
                          </div>
                        </div>
                      }
                    </div>
                    <div className="mt-2">
                      {
                        qrCodeUpdatedAt
                        ?
                        <Text className="text-sm font-medium">
                          QR code atualizado em {moment(qrCodeUpdatedAt).format('DD/MM/YYYY [às] HH:mm:ss')}
                        </Text>
                        :
                        <div className="w-[140px] h-[14px] bg-[#8a8a8a32]" />
                      }
                    </div>
                    {/* Recuperação 1-clique: o cliente gera um QR novo sozinho quando trava */}
                    <div className="mt-6 pt-5" style={{ borderTop: `1px dashed ${theme.palette.divider}` }}>
                      <div className="flex items-center space-x-2">
                        <AutorenewIcon style={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Text className="font-bold text-sm">Problemas para conectar ou o QR não aparece?</Text>
                      </div>
                      <Text className="text-sm mt-1 mb-4" style={{ opacity: 0.7 }}>
                        Gere um novo QR Code para reiniciar a conexão. Você precisará escaneá-lo novamente no WhatsApp do seu celular.
                      </Text>
                      <Button
                        variant="contained"
                        startIcon={isResetting ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                        disabled={isResetting || resetCooldown > 0}
                        onClick={() => setConfirmResetOpen(true)}
                      >
                        {isResetting ? 'Gerando…' : resetCooldown > 0 ? `Aguarde ${resetCooldown}s` : 'Gerar novo QR Code'}
                      </Button>
                    </div>
                  </Collapse>
                  <Collapse in={['hiperapi-v3-cloud-official'].includes(apiVersion) && hasMetaOficial}>
                    <div className="col-span-12 pt-4">
                      <div className="mb-4 border-l-4 pl-3" style={{ borderColor: theme.palette.primary.main }}>
                        <div className="flex items-center space-x-3">
                          <Text className="text-xl font-black">
                            Whatsapp Cloud API Oficial
                          </Text>
                          <span
                            className="px-1 font-black text-xs rounded-sm text-white"
                            style={{ backgroundColor: theme.palette.primary.main }}
                          >BETA</span>
                        </div>
                        <Text className="font-light">
                          Conexão oficial com a api do Whatsapp
                        </Text>
                      </div>
                      {
                        showEmbeddedSignup &&
                        <div className="mb-7 max-w-[435px]">
                          <MetaEmbeddedSignupButton onConnected={getApiData} />
                          <div className="flex items-center space-x-3 mt-6 mb-1">
                            <div className="flex-1 h-px" style={{ backgroundColor: theme.palette.divider }} />
                            <Text className="text-xs" style={{ opacity: 0.6 }}>ou conectar manualmente</Text>
                            <div className="flex-1 h-px" style={{ backgroundColor: theme.palette.divider }} />
                          </div>
                        </div>
                      }
                      <div className="space-y-7">
                        <div>
                          <Text>
                            Number ID
                          </Text>
                          <Text className="text-sm">
                            Identificação do número de telefone
                          </Text>
                          <div className="mt-2 max-w-[435px]">
                            <TextFieldCustom
                              size="large"
                              label="Number ID"
                              disabled={isLoading}
                              value={whatsappBusinessNumberId}
                              onChange={e => setWhatsappBusinessNumberId(e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <Text>
                            Business ID
                          </Text>
                          <Text className="text-sm">
                            Identificação da conta do Whatsapp Business
                          </Text>
                          <div className="mt-2 max-w-[435px]">
                            <TextFieldCustom
                              size="large"
                              label="Business ID"
                              disabled={isLoading}
                              value={whatsappBusinessId}
                              onChange={e => setWhatsappBusinessId(e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <Text>
                            Token de acesso
                          </Text>
                          <div className="mt-2 max-w-[750px]">
                            <TextFieldCustom
                              size="large"
                              label="Token de acesso"
                              disabled={isLoading}
                              value={whatsappBusinessToken}
                              placeholder={businessToken && businessToken.includes('•') ? 'Token oculto por segurança — cole um novo se quiser trocar' : undefined}
                              onChange={e => {
                                setWhatsappBusinessToken(e.target.value)
                                setTokenSaveError('')
                              }}
                            />
                          </div>
                          {
                            tokenSaveError &&
                            <Text className="text-xs mt-2" style={{ color: '#ef4444' }}>
                              {tokenSaveError}
                            </Text>
                          }
                        </div>
                        <div>
                          <Button variant="text" disabled={isLoading} onClick={saveApiOficialCredentials}>
                            SALVAR CREDENCIAIS
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Collapse>
                </div>
                <div className="col-span-1 xl:col-span-7 xl:pt-4">
                  <Collapse in={['hiperapi-v3-cloud-official'].includes(apiVersion) && hasMetaOficial}>
                    <div className="px-8">
                      <div className="py-10 flex justify-center">
                        <img className="w-[60%]" src={theme.isDark ? metaWhite : metaPrimary} />
                      </div>
                      <Paper>
                        <div className="p-4 md:p-10">
                          <div className="flex space-x-4">
                            <div>
                              <div className="w-[45px] h-[45px] rounded-full flex items-center justify-center pl-[2px] pb-[2px]" style={{ backgroundColor: theme.palette.primary.main }}>
                                <WhatsAppIcon style={{ fontSize: 28 }} className="text-white" />
                              </div>
                            </div>
                            <div>
                              <Text className="font-light">API OFICIAL</Text>
                              <Text className="text-sm md:text-2xl">{api}</Text>
                              <Text className="flex items-center space-x-2">
                                <CheckCircleIcon style={{ color: theme.palette.primary.main, fontSize: 26 }} />
                                <span className="block">Servidor</span>
                                <Text className="font-black" style={{ color: theme.palette.primary.main }}>Meta Cloud API Oficial v24.0</Text>
                              </Text>
                            </div>
                          </div>
                        </div>
                      </Paper>
                      <div className="mt-6">
                        <MetaOficialBenefits heading="Vantagens da API Oficial" />
                      </div>
                    </div>
                  </Collapse>
                  <Collapse in={API_VERSIONS_NEED_QRCODE.includes(apiVersion)}>
                    <div>
                      <Text className="font-light text-2xl">Conectar aparelho</Text>
                      <ol className="space-y-6 mt-6">
                        <li className="">
                          <Text className="font-light text-xl">
                            <span className="font-black">1.</span> Abra o Whatsapp no seu celular
                          </Text>
                        </li>
                        <li className="">
                          <Text className="font-light text-xl">
                            <span className="font-black">2.</span> Toque em Mais opções <span>:</span> ou Configurações <span>O</span> e selecione Aparelhos conectados.
                          </Text>
                        </li>
                        <li className="">
                          <Text className="font-light text-xl">
                            <span className="font-black">3.</span> Toque em Conectar um aparelho.
                          </Text>
                        </li>
                        <li className="">
                          <Text className="font-light text-xl">
                            <span className="font-black">4.</span> Aponte o celular para esta tela para capturar o QR code.
                          </Text>
                        </li>
                      </ol>
                      <div className="mt-8">
                        <Paper>
                          <div className="p-4 md:p-10">
                            <div className="flex space-x-4">
                              <div>
                                <StorageIcon style={{ fontSize: 40 }} />
                              </div>
                              <div>
                                <Text className="font-light">API</Text>
                                <Text className="text-sm md:text-2xl">{api}</Text>
                                <Text className="flex items-center space-x-2">
                                  <span className="block">Servidor</span>
                                  {server !== '' ? <span>{server}</span> : <span className="block h-[20px] w-[20px] bg-white bg-opacity-[0.2]" />}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </Paper>
                      </div>
                    </div>
                  </Collapse>
                </div>
                {['hiperapi-v3-cloud-official'].includes(apiVersion) && !hasMetaOficial && (
                  <div className="col-span-12">
                    <MetaOficialUpgradeNotice upgradeContact={upgradeContact} />
                  </div>
                )}
              </div>
            </div>
          </Paper>
        </Paper>
      </div>

      <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)}>
        <DialogTitle>Gerar um novo QR Code?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A conexão atual será reiniciada e um novo QR Code será gerado. Você precisará escaneá-lo novamente com o WhatsApp do seu celular.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<AutorenewIcon />} onClick={handleResetSession}>
            Gerar novo QR Code
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!resetSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={() => setResetSnackbar('')}
        message={resetSnackbar}
      />
    </div>
  )
}
export default DeviceDisconnected